import { Component } from "react";
import { RouteComponentProps } from "react-router";
import { Redirect } from "react-router-dom";

import { NearbyCountiesPanelContent, RegionSet } from "@app/region-sets";
import { RegionSetStatusType } from "../enumerators";
import CreateProject from "./createProject";
import { CountyStatus } from "./interfaces";

import { County } from "@app/geography";
import LookupRegionSet from "@app/region-sets/editor/lookupRegionSet";
import { FlyoutLink } from "@app/shared/Flyouts";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags";
import { ServiceAreaEditor } from "@app/region-sets/new-editor/ServiceAreaEditor";
import WizardStep from "./WizardStep";
import ServiceAreaContextProvider from "@app/region-sets/new-editor/ServiceAreaEditorContext";

type CreateProjectStepServiceAreaProps = {
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  baseUrl: string;
  handleCountyStatusUpdates: (countyStatuses: CountyStatus[]) => void;
  handleCountyStatusForCheckedState: (counties: County[]) => void;
  isZipProject: boolean;
  lookupRegionSet: LookupRegionSet;
  updateLookupRegionSet: (lookupRegionSet: LookupRegionSet) => void;
  onRegionSetChange: (regionSet: RegionSet) => void;
  onSubmitNearbyCounties: (updateCounties: County[], regionStatus: RegionSetStatusType) => void;
  progressList: string[];
  registerNearbyCountiesTriggerClose: (triggerCloser: () => void) => void;
  serviceAreaRegionSet: RegionSet;
  updateCounties: County[];
  currentIndex: number;
} & RouteComponentProps & FeatureFlagContextProps;

interface CreateProjectStepServiceAreaState {
  triggerEditorComplete?: () => void;
  restartApp: boolean;
  isNextDisabled: boolean;
}

class WrappedCreateProjectStepServiceArea extends Component<CreateProjectStepServiceAreaProps, CreateProjectStepServiceAreaState> {
  constructor(props: CreateProjectStepServiceAreaProps) {
    super(props);

    this.state = {
      isNextDisabled: true,
      restartApp: !props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step),
    };
  }

  public render() {

    if (this.state.restartApp) {
      return <Redirect to={`${this.props.baseUrl}/1`} />;
    }

    return (
      <ServiceAreaContextProvider
        countyStatuses={this.props.createProjectModel.countyStatuses}
        isZipProject={this.props.isZipProject}
        projectName={this.props.createProjectModel.name}
        regionSet={this.props.serviceAreaRegionSet}
      >
        <WizardStep
          {...this.props}
          actionButton={this.renderAddNearbyCounties}
          heading="Create a New Project"
          subheading="Define Analysis :: Service Area"
          baseUrl={this.props.baseUrl}
          prevUrl={(this.props.currentIndex - 1).toString()}
          nextUrl={(this.props.currentIndex + 1).toString()}
          progressList={this.props.progressList}
          onNextClick={this.handleNextClick}
          onPrevClick={this.handlePrevClick}
          onWizardStepsNavigation={this.handlePrevClick}
          isNextDisabled={this.state.isNextDisabled}
        >
        <ServiceAreaEditor
          countyStatuses={this.props.createProjectModel.countyStatuses}
          onComplete={this.props.onRegionSetChange}
          onInit={this.handleInit}
          onUpdate={this.handleRegionUpdate}
          regionSet={this.props.serviceAreaRegionSet}
          handleCountyStatusUpdates={this.props.handleCountyStatusUpdates}
          updateCounties={this.props.updateCounties}
        />
        </WizardStep>
      </ServiceAreaContextProvider>
    );
  }

  private renderAddNearbyCounties = () => {
    const countySSAs = this.props.createProjectModel
      && this.props.createProjectModel.countyStatuses
      && this.props.createProjectModel.countyStatuses.map((countyStatus) => countyStatus.countySSA);

    if (countySSAs && countySSAs.length) {
      return (
        <FlyoutLink
          classNames="qes-button-secondary mr15 pr-2 pl-2"
          content={{
            body: (<NearbyCountiesPanelContent
              countySSAs={countySSAs}
              onSubmitNearbyCounties={this.props.onSubmitNearbyCounties}
            />),
            title: "Add Nearby Counties",
          }}
          testId="add-nearby-counties-flyout-link"
          size="20"
          onClose={() => this.props.registerNearbyCountiesTriggerClose(() => null)}
        >
          Add Nearby Counties
        </FlyoutLink>
      );
    }

    return null;
  }

  private handleRegionUpdate = (lookupRegionSet: LookupRegionSet) => {
    // For some reason an empty lookupRegionSet.children is not the same as {}, but serializing it works
    if (JSON.stringify(lookupRegionSet.children) !== "{}") {
      this.setState({
        isNextDisabled: false,
      });
    } else {
      this.setState({
        isNextDisabled: true,
      });
    }
    this.props.updateLookupRegionSet(lookupRegionSet);
  }

  private handleInit = (triggerEditorComplete: () => void) => {
    this.setState({ triggerEditorComplete });
  }

  private handleNextClick = () => {
    if (this.state.triggerEditorComplete) {
      this.state.triggerEditorComplete();
    }
  }

  private handlePrevClick = () => {
    if (this.state.triggerEditorComplete && JSON.stringify(this.props.lookupRegionSet) !== "{}" &&
      Object.keys(this.props.lookupRegionSet.children).length > 0) {
      this.state.triggerEditorComplete();
    }
  }
}

const CreateProjectStepServiceArea = withFeatureFlagContext(WrappedCreateProjectStepServiceArea);
export default CreateProjectStepServiceArea;
